import { createContext } from "react";

/**
 * Dialog Configuration Types
 *
 * Four generic dialog templates:
 * - confirm: Simple confirmation dialog (delete, tag actions)
 * - input: Dialog with numeric input field (damage, heal, spend)
 * - text-input: Dialog with text input field (rename, etc.)
 * - select: Dialog with a list of options to choose from
 *
 * Theme options:
 * - positive: Green/success theme (healing, gaining resources)
 * - negative: Red/danger theme (damage, spending resources)
 * - neutral: Default theme (general actions)
 */
export type DialogConfig =
  | {
    type: 'confirm';
    title: string;
    subtitle?: string;
    message: string;
    confirmLabel?: string;
    theme?: 'positive' | 'negative' | 'neutral';
    onConfirm: () => void;
  }
  | {
    type: 'input';
    title: string;
    subtitle?: string;
    message?: string;
    inputLabel?: string;
    confirmLabel?: string;
    theme?: 'positive' | 'negative' | 'neutral';
    onConfirm: (value: number) => void;
  }
  | {
    type: 'text-input';
    title: string;
    subtitle?: string;
    message?: string;
    inputLabel?: string;
    initialValue?: string;
    confirmLabel?: string;
    theme?: 'positive' | 'negative' | 'neutral';
    onConfirm: (value: string) => void;
  }
  | {
    type: 'select';
    title: string;
    subtitle?: string;
    message?: string;
    options: Array<{ id: string; label: string; disabled?: boolean }>;
    confirmLabel?: string;
    theme?: 'positive' | 'negative' | 'neutral';
    onConfirm: (selectedId: string) => void;
  };

/**
 * Dialog Context Value
 *
 * Provides dialog state and control functions to components.
 */
export interface DialogContextValue {
  openDialog: (config: DialogConfig) => void;
  closeDialog: () => void;
  dialogState: DialogConfig | null;
}

/**
 * Context for dialog management
 *
 * Should only be accessed via useDialog() hook, not directly.
 * Will be undefined if used outside of DialogProvider.
 */
export const DialogContext = createContext<DialogContextValue | undefined>(undefined);
